import { useCallback, useMemo } from 'react';
import { useSmartProp } from '@smartfire/js-block-engine';
import { parseRawString } from '../helper-functions';
import type { AllowedTagName, SavedConfig } from '../types';
import { useDecimalSeparatorForNumberDetectionConfig } from './useDecimalSeparatorForNumberDetectionConfig';
import { useExtraStylesConfig } from './useExtraStylesConfig';
import { useNonSpringRelatedAnimationConfig } from './useNonSpringRelatedAnimationConfig';
import { useNumberFormatConfig } from './useNumberFormatConfig';
import { useSpringRelatedAnimationConfig } from './useSpringRelatedAnimationConfig';
import { useTagNameConfig } from './useTagNameConfig';
import {
  DECIMAL_SEPARATORS_FOR_NUMBER_DETECTION,
  DEFAULT_ANIMATION_DELAY,
  DEFAULT_SPRING_FRICTION,
  DEFAULT_SPRING_TENSION,
  SPECIAL_DELIMITER,
} from '../constants';
import { getPreviousIndices, validate01StringLength, validateArrayLength } from '../utils';

interface UseEditableTextWithAniNumParams {
  smartProp: string;
  /**
   * should not be empty
   */
  allowedTagNames: readonly AllowedTagName[];
  /**
   * should not be empty
   */
  allowedDecimalSeparators: readonly string[];
  /**
   * should not be empty
   */
  allowedThousandsSeparators: readonly string[];
  defaultAnimationDuration: number | undefined;
}

/**
 * `allowedTagNames`, `allowedDecimalSeparators`, `allowedThousandsSeparators` should not be empty
 */
export const useEditableTextWithAniNumParams = ({
  smartProp,
  allowedTagNames,
  allowedDecimalSeparators,
  allowedThousandsSeparators,
  defaultAnimationDuration,
}: UseEditableTextWithAniNumParams) => {
  // ===== call `useSmartProp` =====

  // CAUTION! Saved states (`fetchXXX`) could be `undefined`, the typing is not completely truthful
  const [fetchedRawString, updateRawString] = useSmartProp<string>(smartProp);
  const [fetchedSavedConfig, updateSavedConfig] = useSmartProp<SavedConfig>(`${smartProp}Config`);

  // ===== validate `fetchXXX` from smart props (define `xxx` from `fetchXXX`) =====

  const rawString = fetchedRawString ?? '';

  let tagName: AllowedTagName;

  if (allowedTagNames.includes(fetchedSavedConfig?.tagName)) {
    tagName = fetchedSavedConfig.tagName;
  } else if (allowedTagNames.includes('p')) {
    tagName = 'p';
  } else if (allowedTagNames.includes('div')) {
    tagName = 'div';
  } else if (allowedTagNames.includes('span')) {
    tagName = 'span';
  } else {
    [tagName] = allowedTagNames;
  }

  const areDecimalPlacesFixed = fetchedSavedConfig?.areDecimalPlacesFixed === true;

  const decimalSeparator = allowedDecimalSeparators.includes(fetchedSavedConfig?.decimalSeparator)
    ? fetchedSavedConfig.decimalSeparator
    : allowedDecimalSeparators[0];

  const thousandsSeparator = allowedThousandsSeparators.includes(
    fetchedSavedConfig?.thousandsSeparator,
  )
    ? fetchedSavedConfig.thousandsSeparator
    : allowedThousandsSeparators[0];

  const decimalSeparatorForNumberDetection = DECIMAL_SEPARATORS_FOR_NUMBER_DETECTION.includes(
    fetchedSavedConfig?.decimalSeparatorForNumberDetection,
  )
    ? fetchedSavedConfig.decimalSeparatorForNumberDetection
    : DECIMAL_SEPARATORS_FOR_NUMBER_DETECTION[0];

  const animationDuration = fetchedSavedConfig?.animationDuration;

  const springFriction = fetchedSavedConfig?.springFriction ?? DEFAULT_SPRING_FRICTION;

  const springTension = fetchedSavedConfig?.springTension ?? DEFAULT_SPRING_TENSION;

  const defaultAnimationDelay =
    fetchedSavedConfig?.defaultAnimationDelay ?? DEFAULT_ANIMATION_DELAY;

  const skeleton = useMemo(
    () => parseRawString({ rawString, decimalSeparatorForNumberDetection }),
    [decimalSeparatorForNumberDetection, rawString],
  );

  const detectedNumberSubstrings = useMemo(
    () => skeleton.filter((x) => x[1] >= 0).map((x) => x[0]),
    [skeleton],
  );

  const joinedDetectedNumberSubstrings = detectedNumberSubstrings.join(SPECIAL_DELIMITER);

  const stringifiedLocalAnimationDelays = useMemo(
    () =>
      JSON.stringify(
        validateArrayLength(
          fetchedSavedConfig?.stringifiedLocalAnimationDelays
            ? JSON.parse(fetchedSavedConfig.stringifiedLocalAnimationDelays)
            : undefined,
          detectedNumberSubstrings.length,
          defaultAnimationDelay,
        ),
      ),
    [
      defaultAnimationDelay,
      detectedNumberSubstrings.length,
      fetchedSavedConfig?.stringifiedLocalAnimationDelays,
    ],
  );

  const localIsAnimationDisabled = validate01StringLength(
    fetchedSavedConfig?.localIsAnimationDisabled,
    detectedNumberSubstrings.length,
  );

  const isBold = fetchedSavedConfig?.isBold;
  const isItalic = fetchedSavedConfig?.isItalic;
  const isUnderlined = fetchedSavedConfig?.isUnderlined;
  const textColor = fetchedSavedConfig?.textColor;
  const backgroundColor = fetchedSavedConfig?.backgroundColor;
  const textAlign = fetchedSavedConfig?.textAlign;

  const savedState: SavedConfig = useMemo(
    () => ({
      tagName,
      areDecimalPlacesFixed,
      decimalSeparator,
      thousandsSeparator,
      decimalSeparatorForNumberDetection,
      animationDuration,
      springFriction,
      springTension,
      defaultAnimationDelay,
      stringifiedLocalAnimationDelays,
      localIsAnimationDisabled,
      isBold,
      isItalic,
      isUnderlined,
      textColor,
      backgroundColor,
      textAlign,
    }),
    [
      tagName,
      areDecimalPlacesFixed,
      decimalSeparator,
      thousandsSeparator,
      decimalSeparatorForNumberDetection,
      animationDuration,
      springFriction,
      springTension,
      defaultAnimationDelay,
      stringifiedLocalAnimationDelays,
      localIsAnimationDisabled,
      isBold,
      isItalic,
      isUnderlined,
      textColor,
      backgroundColor,
      textAlign,
    ],
  );

  // ===== onChangeRawString =====

  const onChangeRawString = useCallback(
    (v: string) => {
      const newDetectedNumbersInString = parseRawString({
        rawString: v,
        decimalSeparatorForNumberDetection,
      })
        .filter((x) => x[1] >= 0)
        .map((x) => x[0]);

      const numOfNewDetectedNumbers = newDetectedNumbersInString.length;

      const localAnimationDelays = JSON.parse(stringifiedLocalAnimationDelays);

      const previousIndices = getPreviousIndices(
        detectedNumberSubstrings,
        newDetectedNumbersInString,
      );

      updateRawString(v);
      updateSavedConfig({
        ...savedState,
        localIsAnimationDisabled: Array(numOfNewDetectedNumbers)
          .fill('0')
          .map((x, i) =>
            previousIndices[i] >= 0 ? localIsAnimationDisabled[previousIndices[i]] : x,
          )
          .join(''),
        stringifiedLocalAnimationDelays: JSON.stringify(
          Array(numOfNewDetectedNumbers)
            .fill(defaultAnimationDelay)
            .map((x, i) =>
              previousIndices[i] >= 0 ? localAnimationDelays[previousIndices[i]] : x,
            ),
        ),
      });
    },
    [
      savedState,
      updateRawString,
      updateSavedConfig,
      decimalSeparatorForNumberDetection,
      defaultAnimationDelay,
      detectedNumberSubstrings,
      stringifiedLocalAnimationDelays,
      localIsAnimationDisabled,
    ],
  );

  // ===== return =====

  return {
    rawString,
    onChangeRawString,
    joinedDetectedNumberSubstrings,
    ...useDecimalSeparatorForNumberDetectionConfig(savedState, updateSavedConfig),
    ...useExtraStylesConfig(savedState, updateSavedConfig),
    ...useNonSpringRelatedAnimationConfig(savedState, updateSavedConfig),
    ...useNumberFormatConfig(savedState, updateSavedConfig),
    ...useSpringRelatedAnimationConfig(savedState, updateSavedConfig, defaultAnimationDuration),
    ...useTagNameConfig(savedState, updateSavedConfig),
  };
};
